import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import NavPills from 'components/NavPills/NavPills';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config/config.json';
import DeliveryRequestList from './components/DeliveryRequestList';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useSearchParams } from 'react-router-dom';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 100,
    '& .MuiTabs-root': {
      zIndex: 200,
    },
    '& .MuiTab-root': {
      zIndex: 200,
    },
  },
  card: {
    marginTop: '30px',
    marginBottom: '30px',
    width: '100%',
  },
  cardHeader: {
    width: 'auto',
    textAlign: 'center',
    marginLeft: '20px',
    marginRight: '20px',
    marginTop: '-40px',
    padding: '20px 0',
    marginBottom: '15px',
  },
}));

export default function OrdersPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const sessionToken = localStorage.getItem('session_token');
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  useDocumentTitle(t('orders.your_orders'));

  // Get delivery requests
  const { data: deliveryRequestsResponse, isLoading: isLoadingDeliveryRequests } = useQuery({
    queryKey: ['deliveryRequests'],
    queryFn: async () => {
      const response = await axios.get(`${config.backend_base_url}rs/trade/my`, {
        headers: { 'session-token': sessionToken },
      });
      return response.data;
    },
    enabled: !!sessionToken,
  });

  // Extract the trades array from the response
  const deliveryRequests = deliveryRequestsResponse?.trades || [];

  // Filter delivery requests by status
  const filteredDeliveryRequests = {
    rerouteRequired: deliveryRequests.filter((request) => request.reroute_required === 1),
    inProgress: deliveryRequests.filter(
      (request) => request.completed === 0 && request.reroute_required === 0
    ),
    completed: deliveryRequests.filter((request) => request.completed === 1),
  };

  console.warn('test', deliveryRequests);

  if (isLoadingDeliveryRequests) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card className={classes.card}>
              <CardHeader color="primary" className={classes.cardHeader}>
                <h4>{t('orders.your_orders')}</h4>
              </CardHeader>
              <CardBody>
                <NavPills
                  queryKey="status"
                  alignCenter
                  color="primary"
                  active={statusParam === 'in_progress' ? 1 : statusParam === 'completed' ? 2 : 0}
                  tabs={[
                    {
                      tabName: 'request_delivery',
                      tabButton: `${t('orders.request_delivery')} (${filteredDeliveryRequests.rerouteRequired.length})`,
                      tabContent: (
                        <DeliveryRequestList requests={filteredDeliveryRequests.rerouteRequired} />
                      ),
                    },
                    {
                      tabName: 'in_progress',
                      tabButton: `${t('orders.in_progress')} (${filteredDeliveryRequests.inProgress.length})`,
                      tabContent: (
                        <DeliveryRequestList requests={filteredDeliveryRequests.inProgress} />
                      ),
                    },
                    {
                      tabName: 'completed',
                      tabButton: `${t('orders.completed')} (${filteredDeliveryRequests.completed.length})`,
                      tabContent: (
                        <DeliveryRequestList requests={filteredDeliveryRequests.completed} />
                      ),
                    },
                  ]}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      <BubbleContainer pageKey="deliveryrequest" />
    </div>
  );
}
